import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControllerUpdateType,
  IControllerAvailableUpdateInfo,
} from 'lavva.exalushome/build/js/Services/Updates/IUpdateInfo';
import { UpdateErrorCode } from 'lavva.exalushome/build/js/Services/Updates/IUpdatesService';
import { ControllerUpdateNotAvailableInfo } from 'lavva.exalushome/build/js/Services/Updates/UpdatesService';
import { IconWarning } from '../../../../../assets/icons/icon-warning';
import { DialogConfirmation, EmptyStateBox, Header, NavHeader, Page } from '../../../../../components';
import TextInfo from '../../../../../components/text-info';
import { usePreventBack } from '../../../../../hooks/router/use-navigation-guard';
import { useExalusContext } from '../../../context';
import { PopupType } from '../../../enums/controller';
import { useLoggedUser } from '../../../hooks/common/logged-user';
import { langCode } from '../../../utils/updates';
import { UpdateFailedPopup } from '../components/update-failed-popup/update-failed-popup';
import { useControllerSettings } from '../hooks/use-controller-settings';
import { useControllerUpdates } from '../hooks/use-controller-updates';
import { ProcessKey } from '../types';
import { getProcessKey } from '../utils/controller';
import './index.scss';

const ControllerUpdates: React.FC = () => {
  const { t, i18n } = useTranslation('device-info');
  const { t: ti } = useTranslation('installation');
  const { t: tc } = useTranslation('common');
  const { popup, popupType, setPopup, showDialog, handleConfirm } = useControllerSettings();
  const { synchronized } = useExalusContext();
  const {
    controllerUpdate,
    loading,
    updateFailedPopup,
    canCancelOrGetPendingUpdate,
    setUpdateFailedPopup,
    checkForControllerUpdates,
    handleDownloadOrUpdateController,
    installAllUpdates,
    checkIfCanCancel,
    cancelControllerUpdateAndTryAgain,
    checkControllerPendingUpdateProgressAsync,
  } = useControllerUpdates();
  const { user: userExalus } = useLoggedUser();
  usePreventBack(loading, t('exalus.params.Version.noBackMessage'));
  const showUpdates = controllerUpdate?.UpdatesAvailable.length || controllerUpdate?.UpdatesNotAvailable.length;

  useEffect(() => {
    if (synchronized) {
      checkForControllerUpdates();
      checkIfCanCancel();
    }
  }, [synchronized]);

  useEffect(() => {
    if (canCancelOrGetPendingUpdate && controllerUpdate && controllerUpdate.UpdatesAvailable.length > 0) {
      const softUpdate = controllerUpdate.UpdatesAvailable.find(
        (update) => update.UpdateType === ControllerUpdateType.SoftwareUpdate,
      );

      if (softUpdate) checkControllerPendingUpdateProgressAsync(softUpdate, softUpdate.IsDownloadOnly);
    }
  }, [controllerUpdate, canCancelOrGetPendingUpdate]);

  const additionalControllerButtonsVisible = useMemo(() => {
    if (userExalus?.AccessLevel) return userExalus.AccessLevel >= 30;
    return false;
  }, [userExalus]);

  const renderEmptyUpdates = () => {
    if (!controllerUpdate) return null;
    return (
      <EmptyStateBox
        btnText={t('exalus.params.Version.checkForUpdates')}
        content={t('recoveryMode.status.FIRMWARE_IS_UP_TO_DATE')}
        icon={<IconWarning />}
        onClick={checkForControllerUpdates}
      />
    );
  };

  return (
    <Page
      isStickyHeader
      className="controller-updates"
      contentClassName="max-width-desktop"
      header={
        <>
          <NavHeader />
          <Header title={ti('softUpdate')} />
        </>
      }
    >
      {showUpdates ? (
        <>
          {controllerUpdate.UpdatesAvailable.length > 0 && (
            <div className="controller-updates">
              <div className="controller-updates--content">
                {controllerUpdate.UpdatesAvailable.map((update: IControllerAvailableUpdateInfo, i: number) => {
                  const key = getProcessKey(update);

                  return (
                    <div key={i} className="version-container">
                      <div className="resource-header">
                        <h3>{`${t(
                          `exalus.params.Version.${
                            update.UpdateType === ControllerUpdateType.SoftwareUpdate
                              ? 'SoftwareUpdate'
                              : 'RadioFirmware'
                          }`,
                        )} ${
                          update.UpdateType !== ControllerUpdateType.SoftwareUpdate ? update.UpdatedResourceName : ''
                        }`}</h3>
                        {key && (
                          <button
                            className="button button--secondary"
                            disabled={key === ProcessKey.Downloading || loading}
                            onClick={() => handleDownloadOrUpdateController(key)}
                          >
                            {t(`exalus.params.Version.${key}`)}
                          </button>
                        )}
                      </div>
                      <div className="version-values">
                        <TextInfo
                          value={update.CurrentResourceVersion}
                          label={`${t('exalus.params.Version.currentVersion')}:`}
                          className="m-b-8"
                        />
                        <TextInfo
                          value={update.NewResourceVersion}
                          label={`${t('exalus.params.Version.newVersion')}:`}
                          className="m-b-8"
                        />
                        <TextInfo
                          value={
                            update.UpdateType !== null
                              ? t(`exalus.params.Version.${ControllerUpdateType[update.UpdateType]}`)
                              : '-'
                          }
                          label={`${t('exalus.params.Version.type')}:`}
                          className="m-b-8"
                        />
                      </div>
                      {additionalControllerButtonsVisible &&
                        update.UpdatedResourceName.get(langCode[i18n.language]) ===
                          t('exalus.params.Version.SoftwareUpdate') && (
                          <button
                            className="button button--secondary m-t-24"
                            onClick={() => showDialog(PopupType.Rollback)}
                          >
                            {ti('exalus.rollbackControllerUpdate')}
                          </button>
                        )}
                      <hr />
                    </div>
                  );
                })}
                <button className="button button--secondary m-b-24" onClick={installAllUpdates}>
                  {`${t('exalus.params.Version.updateAllStart')} (${controllerUpdate.UpdatesAvailable.length})`}
                </button>
              </div>
            </div>
          )}
          {controllerUpdate.UpdatesNotAvailable.length > 0 && (
            <div className="controller-updates m-t-24">
              <div className="controller-updates--content">
                {controllerUpdate.UpdatesNotAvailable.map((update: ControllerUpdateNotAvailableInfo, i: number) => (
                  <div key={i} className="version-container">
                    <h3>{`${t(
                      `exalus.params.Version.${
                        update.UpdateType === ControllerUpdateType.SoftwareUpdate ? 'SoftwareUpdate' : 'RadioFirmware'
                      }`,
                    )} ${
                      update.UpdateType !== ControllerUpdateType.SoftwareUpdate
                        ? update.UpdatedResourceName.get(langCode[i18n.language])
                        : ''
                    }`}</h3>
                    <div className="version-values">
                      <TextInfo
                        value={
                          update.ResponseResult
                            ? t(`exalus.updateResult.${UpdateErrorCode[update.ResponseResult.Type]}`)
                            : '-'
                        }
                        label={`${t('exalus.params.Version.status')}:`}
                        className="m-b-8"
                      />
                      <TextInfo
                        value={update.CurrentResourceVersion}
                        label={`${t('exalus.params.Version.currentVersion')}:`}
                        className="m-b-8"
                      />
                      <TextInfo
                        value={
                          update.UpdateType !== null
                            ? t(`exalus.params.Version.${ControllerUpdateType[update.UpdateType]}`)
                            : '-'
                        }
                        label={`${t('exalus.params.Version.type')}:`}
                        className="m-b-8"
                      />
                    </div>
                    {additionalControllerButtonsVisible &&
                      update.UpdateType === ControllerUpdateType.SoftwareUpdate && (
                        <button
                          className="button button--secondary m-t-24"
                          onClick={() => showDialog(PopupType.Rollback)}
                        >
                          {ti('exalus.rollbackControllerUpdate')}
                        </button>
                      )}
                    <hr />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        renderEmptyUpdates()
      )}
      <DialogConfirmation
        show={popup}
        setShow={setPopup}
        header={ti(`exalus.${popupType?.toLowerCase()}DialogTitle`)}
        content={ti(`exalus.${popupType?.toLowerCase()}DialogContent`)}
        primaryBtnText={ti(`exalus.${popupType?.toLowerCase()}Controller`)}
        secondaryBtnText={tc('buttons.cancel')}
        secondaryBtnAction={() => setPopup(false)}
        primaryBtnAction={handleConfirm}
      />
      <UpdateFailedPopup
        show={updateFailedPopup}
        setShow={setUpdateFailedPopup}
        cancelControllerUpdateAndTryAgain={cancelControllerUpdateAndTryAgain}
      />
    </Page>
  );
};

export default ControllerUpdates;
